import React, { useState, ChangeEvent } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { IoHome } from "react-icons/io5";
import signUpImg from "../assets/img/bg8.jpg";
import logo1 from "../assets/img/rsilogo.png";
import { FaLocationDot } from "react-icons/fa6";
import useScrollToTop from "../components/useScrollToTop";
import { useDispatch } from "react-redux";
import { addProfile } from "../redux/slices/ProfileSlice";
import { account, database } from "../appWrite";
import { Query } from "appwrite";
import { FaEye, FaEyeSlash } from "react-icons/fa";

// Define the shape of the form data
interface FormData {
  userEmail: string;
  userPassword: string;
}

const Login: React.FC = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility

  const [formData, setFormData] = useState<FormData>({
    userEmail: "",
    userPassword: "",
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Check if a session already exists
      let session;
      try {
        session = await account.getSession("current");
        account.deleteSessions();
      } catch (err) {
        // No active session found, so we proceed to create a new one
        session = await account.createEmailPasswordSession(
          formData.userEmail,
          formData.userPassword
        );
      }

      // Get the user information
      const user = await account.get();
      console.log(user);

      // Fetch user attributes from the database
      const userAttributes = await database.listDocuments(
        "67ce8a4a0032e13c6368", // Replace with your actual database ID
        "67ce8a5b002afcaf4f27", // Replace with your actual collection ID
        [Query.equal("userId", user.$id)]
      );

      // Check if any user attributes were found
      const userRole =
        userAttributes.documents.length > 0
          ? userAttributes.documents[0].role
          : null;

      // Dispatch the profile information including the role
      dispatch(
        addProfile({
          id: user.$id,
          name: user.name,
          token: session.$id, // This is just an example, adjust based on your needs
          role: userRole, // Include the role
          email: user.email,
        })
      );

      // Save modal state in session storage
      sessionStorage.setItem("modal_shown", "true");

      // Navigate
      if (userRole === "admin") {
        navigate("/admin-dashboard");
      } else {
        navigate("/");
      }
      setIsLoading(false);
    } catch (error: any) {
      const errorMsg = error.message || "An error occurred";
      showAlert(errorMsg);
      setIsLoading(false);
    }
  };

  function showAlert(msg: string) {
    const errDiv = document.querySelector(".error-div");

    if (errDiv) {
      errDiv.textContent = msg;
      errDiv.classList.toggle("hidden");

      setTimeout(() => {
        errDiv.classList.toggle("hidden");
      }, 2000);
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="w-full h-screen flex flex-col md:flex-row"
    >
      <div className="hidden md:flex w-full md:w-1/2 relative">
        <img src={logo1} alt="Logo" className="w-16 ml-4 mt-2 absolute" />
        <img
          src={signUpImg}
          className="w-full h-full object-cover"
          alt="bg-img"
        />
        <h3 className="absolute bottom-8 left-4 text-lg font-semibold text-gray-400 flex items-center gap-1">
          Rehoboth
          <FaLocationDot className="animate-pulse" />
        </h3>
      </div>
      <div className="w-full md:w-1/2 lg:pt-10 bg-slate-50 flex flex-col justify-center p-8">
        <div className="flex bg-slate-50 justify-between items-center mb-8 md:mb-4 md:pt-12">
          <h1 className="text-3xl font-semibold">Log in</h1>
          <Link to="/">
            <IoHome className="text-3xl text-primary hover:text-primary-dark" />
          </Link>
        </div>
        <div className="error-div hidden mb-4 bg-primarytext-primary text-white w-[76%] px-6 mx-auto py-2 rounded-lg"></div>
        <div className="bg-white p-6 mt-[4rem] rounded-lg shadow-xl">
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              name="userEmail"
              id="userEmail"
              placeholder="Email address"
              required
              value={formData.userEmail}
              onChange={handleChange}
              className="border border-gray-400 py-2 px-4 w-full rounded mb-3"
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="userPassword"
                id="userPassword"
                placeholder="Password"
                required
                value={formData.userPassword}
                onChange={handleChange}
                className="border border-gray-400 py-2 px-4 w-full rounded mb-3 pr-10"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute top-[1.4rem] right-3 transform -translate-y-1/2 text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <p className="mt-2 flex gap-2">
              Don't have an account?{" "}
              <Link className="font-semibold text-primary" to="/signup">
                Sign up
              </Link>
            </p>
            <button
              disabled={isLoading}
              className={`w-full py-2 mt-5 bg-primary text-white font-semibold rounded hover:bg-primary-dark"
              type="submit ${isLoading ? "animate-pulse" : ""}`}
            >
              {isLoading ? "Loading..." : "Submit"}
            </button>
          </form>
        </div>
        <h3 className="font-bold mt-8 text-center text-primary md:mt-32">
          Rehoboth
        </h3>
      </div>
    </motion.div>
  );
};

export default Login;
